import React, { useState, useEffect } from "react";
import Spinner from "./Spinner";
import { useHistorialSolicitudes } from "../../cambios-contractuales/historial-solicitudes/useHistorialSolicitudes";
import {
  faClock,
  faCircleCheck,
  faCircleXmark,
  faCircleInfo,
  faUser,
  faRobot,
  faArrowCircleLeft,
  faBuilding,
  faBriefcase,
  faCalendarAlt,
  faIdCard
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatFecha } from "../../utils/funciones";
import PropTypes from "prop-types";

const formatAuthor = (rawAuthor) => {
  if (!rawAuthor) return { name: "Usuario", isSystem: false };
  const str = String(rawAuthor).trim();
  if (["-1", "sistema", "system", "administrador del sistema", "admin sistema"].includes(str.toLowerCase())) {
    return { name: "Sistema (Automático)", isSystem: true };
  }
  return { name: str, isSystem: false };
};

export const Timeline = props => {
  const { id, volver, endpoint = "gethistorialsolicitudes" } = props;
  const [events, setEvents] = useState([]);
  const [headerSummary, setHeaderSummary] = useState(null);
  const { traerHistorial, historial } = useHistorialSolicitudes();

  Timeline.propTypes = {
    id: PropTypes.number.isRequired,
    volver: PropTypes.func.isRequired,
    endpoint: PropTypes.string,
  };

  useEffect(() => {
    traerHistorial({ id, endpoint });
  }, [id, endpoint]);

  useEffect(() => {
    if (!historial) {
      return;
    }

    const eventos = historial.map(item => {
      const rawFecha = item.created_at || item.fecha_actualiza_proceso || "";
      const [datePart, timePart] = rawFecha.includes(" ") ? rawFecha.split(" ") : [rawFecha, ""];

      // Construcción del nombre del autor e ID SAP
      const nombreUsuario = item.usuario
        ? `${item.usuario.nombre || ""} ${item.usuario.apellido || ""}`.trim()
        : item.nombre_completo_autor || item.usuario?.nombre || "";
      const idSapUsuario = item.id_sap_creador || item.usuario?.cod_sap || item.usuario?.cod_intranet;

      let autorFormateado = "SIN REGISTRO DE USUARIO";
      if (nombreUsuario && idSapUsuario) {
        autorFormateado = `${nombreUsuario} / ID SAP: ${idSapUsuario}`;
      } else if (nombreUsuario) {
        autorFormateado = nombreUsuario;
      } else if (idSapUsuario) {
        autorFormateado = `SIN REGISTRO DE USUARIO / ID SAP: ${idSapUsuario}`;
      }

      autorFormateado = autorFormateado.toUpperCase();

      const tiempoRelativo = getTiempoRelativo(datePart, timePart);

      return {
        status: item.estado_solicitud_cambio_contractual?.nombre || item.estado_actual || item.estado_nombre || "Estado desconocido",
        date: datePart,
        time: timePart,
        author: item.responsable || item.usuario?.nombre || item.nombre_completo_autor || "Usuario",
        authorSapId: item.id_sap_creador || item.id_sap_autor || item.usuario?.id_sap || item.persona?.id_sap || null,
        description: item.observaciones || "",
        nombre_apellido: item.nombre_apellido || null,
        unidad_organizativa: item.unidad_organizativa || null,
        cargo_actual: item.cargo_actual || null,
        fecha_ingreso: item.fecha_ingreso || null,
      };
    });

    setEvents(eventos);

    const primerElem = historial[0];
    if (primerElem && (primerElem.nombre_apellido || primerElem.unidad_organizativa || primerElem.cargo_actual)) {
      setHeaderSummary({
        nombre_apellido: primerElem.nombre_apellido,
        unidad_organizativa: primerElem.unidad_organizativa,
        cargo_actual: primerElem.cargo_actual,
        fecha_ingreso: primerElem.fecha_ingreso,
      });
    } else {
      setHeaderSummary(null);
    }
  }, [historial]);

  const getStatusIconAndColor = (statusName, isLatest) => {
    let baseColor = "#1976d2";
    let icon = faCircleInfo;

    if (statusName) {
      const lower = statusName.toLowerCase();
      if (lower.includes("aprobad") || lower.includes("aprobación")) {
        baseColor = "#2e7d32";
        icon = faCircleCheck;
      } else if (lower.includes("rechazad")) {
        baseColor = "#d32f2f";
        icon = faCircleXmark;
      }
    }

    if (!isLatest) {
      return {
        icon,
        color: "#6c757d",
        borderColor: "#ced4da",
        isLatest: false
      };
    }

    return {
      icon,
      color: baseColor,
      borderColor: baseColor,
      isLatest: true
    };
  };

  const getTiempoRelativo = (dateStr, timeStr) => {
    if (!dateStr) return "";
    try {
      const isoString = timeStr ? `${dateStr}T${timeStr}` : dateStr;
      const fecha = new Date(isoString);
      if (isNaN(fecha.getTime())) return "";

      const ahora = new Date();
      const diffMs = ahora.getTime() - fecha.getTime();
      if (diffMs < 0) return "Hace un momento";

      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return "Hace un momento";
      if (diffMin < 60) return `Hace ${diffMin} min`;

      const diffHs = Math.floor(diffMin / 60);
      if (diffHs < 24) return `Hace ${diffHs} ${diffHs === 1 ? "hora" : "horas"}`;

      const diffDias = Math.floor(diffHs / 24);
      if (diffDias === 1) return "Ayer";
      if (diffDias < 30) return `Hace ${diffDias} días`;

      return "";
    } catch (e) {
      return "";
    }
  };

  const getCommentStyles = (statusName, isLatest) => {
    if (!isLatest) {
      return {
        background: "#f8f9fa",
        borderColor: "#dee2e6",
        textColor: "#6c757d",
      };
    }

    const lower = (statusName || "").toLowerCase();
    if (lower.includes("aprobad") || lower.includes("aprobación")) {
      return {
        background: "#e8f5e9",
        borderColor: "#2e7d32",
        textColor: "#1b5e20",
      };
    }
    if (lower.includes("rechazad")) {
      return {
        background: "#ffebee",
        borderColor: "#d32f2f",
        textColor: "#c62828",
      };
    }
    return {
      background: "#e3f2fd",
      borderColor: "#1976d2",
      textColor: "#0d47a1",
    };
  };

  return (
    <div className="d-flex flex-column w-100">
      {historial === undefined && (
        <div className="d-flex justify-content-center align-items-center py-5" style={{ minHeight: "200px" }}>
          <Spinner clases="m-0" />
        </div>
      )}

      {historial !== undefined && (
        <>
          {/* 1. SECCIÓN INFORMACIÓN POSTULANTE (Fija arriba, fuera del scroll, solo si hay datos) */}
          {headerSummary && (
            <div className="mb-3 flex-shrink-0">
              <div
                className="card border-0 rounded-3 shadow-sm"
                style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}
              >
                <div className="card-body p-3">
                  <div className="row g-2 text-dark" style={{ fontSize: "0.88rem" }}>
                    {headerSummary.nombre_apellido && (
                      <div className="col-12 col-md-6">
                        <FontAwesomeIcon icon={faIdCard} className="me-2" style={{ color: "#2a77b9" }} />
                        <span className="text-muted me-1">Postulante:</span>
                        <strong>{headerSummary.nombre_apellido}</strong>
                      </div>
                    )}
                    {headerSummary.unidad_organizativa && (
                      <div className="col-12 col-md-6">
                        <FontAwesomeIcon icon={faBuilding} className="me-2" style={{ color: "#2a77b9" }} />
                        <span className="text-muted me-1">Unidad organizativa:</span>
                        <strong>{headerSummary.unidad_organizativa}</strong>
                      </div>
                    )}
                    {headerSummary.cargo_actual && (
                      <div className="col-12 col-md-6">
                        <FontAwesomeIcon icon={faBriefcase} className="me-2" style={{ color: "#2a77b9" }} />
                        <span className="text-muted me-1">Cargo:</span>
                        <strong>{headerSummary.cargo_actual}</strong>
                      </div>
                    )}
                    {headerSummary.fecha_ingreso && (
                      <div className="col-12 col-md-6">
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-2" style={{ color: "#2a77b9" }} />
                        <span className="text-muted me-1">Fecha de proceso:</span>
                        <strong>{formatFecha(headerSummary.fecha_ingreso)}</strong>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 2. HISTORIAL DE EVENTOS (La única sección que tiene scrollbar) */}
          {events?.length > 0 && (
            <div
              className="position-relative custom-timeline-scroll pe-2 ps-1 py-1 flex-grow-1"
              style={{
                maxHeight: headerSummary ? "46vh" : "55vh",
                overflowY: "auto",
                overflowX: "hidden",
                scrollbarWidth: "thin",
                scrollbarColor: "#cbd5e1 transparent"
              }}
            >
              <style>{`
                .custom-timeline-scroll::-webkit-scrollbar {
                  width: 6px;
                }
                .custom-timeline-scroll::-webkit-scrollbar-track {
                  background: transparent;
                }
                .custom-timeline-scroll::-webkit-scrollbar-thumb {
                  background: #cbd5e1;
                  border-radius: 4px;
                }
                .custom-timeline-scroll::-webkit-scrollbar-thumb:hover {
                  background: #94a3b8;
                }
              `}</style>

              {events.map((item, index) => {
                const isLatest = index === 0;
                const isLast = index === events.length - 1;
                const { icon, color, borderColor } = getStatusIconAndColor(item.status, isLatest);
                const itemId = Number.parseInt(index, 10);

                return (
                  <div
                    key={itemId}
                    className={`position-relative ${isLast ? "pb-2" : "pb-4"}`}
                    style={{ paddingLeft: "46px" }}
                  >
                    {/* Línea vertical conectora hacia el siguiente evento */}
                    {!isLast && (
                      <div
                        style={{
                          position: "absolute",
                          left: "21px",
                          top: "16px",
                          bottom: "-16px",
                          width: "2px",
                          backgroundColor: "#dee2e6",
                          zIndex: 0
                        }}
                      />
                    )}

                    {/* Círculo con icono */}
                    <div
                      className="position-absolute bg-white rounded-circle d-flex justify-content-center align-items-center shadow-xs"
                      style={{
                        left: "6px",
                        top: "0",
                        width: "32px",
                        height: "32px",
                        border: `2px solid ${borderColor}`,
                        color: color,
                        zIndex: 2
                      }}
                    >
                      <FontAwesomeIcon icon={icon} style={{ fontSize: "0.85rem" }} />
                    </div>

                    {/* Tarjeta de evento */}
                    <div
                      className={`card border-0 rounded-3 ${isLatest ? "shadow-sm border-start border-4" : ""}`}
                      style={
                        isLatest
                          ? { borderLeftColor: `${color} !important`, backgroundColor: "#ffffff" }
                          : { backgroundColor: "#f8f9fa", border: "1px solid #e9ecef", opacity: 0.9 }
                      }
                    >
                      <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                              <h6
                                className="fw-bold m-0"
                                style={{ color: isLatest ? color : "#334155" }}
                              >
                                {item.status}
                              </h6>
                              {isLatest && (
                                <span
                                  className="badge rounded-pill"
                                  style={{
                                    backgroundColor: `${color}15`,
                                    color: color,
                                    border: `1px solid ${color}40`,
                                    fontSize: "0.68rem",
                                    fontWeight: "700",
                                    padding: "4px 8px"
                                  }}
                                >
                                  Estado Actual
                                </span>
                              )}
                            </div>
                            {(() => {
                              const authorInfo = formatAuthor(item.author);
                              const hasSapId = !authorInfo.isSystem && item.authorSapId && String(item.authorSapId).trim() !== "-1";
                              return (
                                <div className="text-muted small d-flex align-items-center gap-1 flex-wrap">
                                  <FontAwesomeIcon
                                    icon={authorInfo.isSystem ? faRobot : faUser}
                                    style={{ color: authorInfo.isSystem ? "#64748b" : "#2a77b9", fontSize: "0.78rem" }}
                                  />
                                  <span
                                    className="fw-semibold text-capitalize"
                                    style={{ color: authorInfo.isSystem ? "#475569" : "#334155" }}
                                  >
                                    {authorInfo.name}
                                  </span>
                                  {hasSapId && (
                                    <span className="text-muted small fw-normal ms-1" style={{ fontSize: "0.75rem" }}>
                                      (ID SAP: {item.authorSapId})
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                          <div className="text-muted small d-flex align-items-center gap-1">
                            <FontAwesomeIcon icon={faClock} />
                            {item.date ? formatFecha(item.date) : ""} {item.time}
                          </div>
                        </div>

                        {item.description && (
                          <div
                            className="p-2 mt-2 rounded text-muted"
                            style={{
                              fontSize: "0.88rem",
                              backgroundColor: isLatest ? "#f8fafc" : "#ffffff",
                              borderLeft: isLatest ? `3px solid ${color}` : "3px solid #ced4da",
                              border: !isLatest ? "1px solid #e9ecef" : undefined,
                              borderLeftWidth: "3px"
                            }}
                          >
                            <div className="fw-bold mb-1" style={{ fontSize: "0.82rem", color: isLatest ? color : "#495057" }}>
                              Observación:
                            </div>
                            <em style={{ whiteSpace: "pre-wrap" }}>{item.description}</em>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {events?.length === 0 && (
            <div
              className="text-center text-muted py-5"
              style={{ minHeight: "180px", display: "flex", justifyContent: "center", alignItems: "center" }}
            >
              No se encontró historial.
            </div>
          )}

          {/* 3. BOTÓN VOLVER (Fijo abajo, fuera del scroll) */}
          <div className="w-100 justify-content-end d-flex pt-3 pb-3 pe-2 mt-2 border-top flex-shrink-0">
            <button
              className="btn btn-light px-4 py-2 fw-bold rounded-pill d-flex align-items-center shadow-sm transition-all"
              style={{ color: "#2a77b9", border: "1px solid #cce0f0" }}
              title="Volver"
              onClick={volver}
            >
              <span className="me-2">Volver</span> <FontAwesomeIcon icon={faArrowCircleLeft} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};
